import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  ColDef,
  GetContextMenuItemsParams,
  GridReadyEvent,
  ICellRendererParams,
  IServerSideGetRowsParams,
  PaginationChangedEvent,
  SortModelItem,
} from '@ag-grid-community/core'
import { ModuleRegistry } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model'
import {
  Button,
  Input,
  Modal,
  Select,
  Tag,
  Tooltip,
} from '@surecompanies/core_components'
import DatePicker from 'antd/es/date-picker'
import type { Dayjs } from 'dayjs'
import DeleteOutlined from '@ant-design/icons/DeleteOutlined'
import { useDebounce } from 'hooks/useDebounce'
import { relativeTimeString } from 'enrollment-platform/utils/relative-time-string'
import { PlanOverrideAction } from '../../../enums/plan-override-action.enum'
import type {
  LocationLevelHistoryEntryDto,
  LocationLevelHistoryQueryDto,
  LocationLevelHistorySortField,
} from '../../../api/dto/location-level-history.dto'
import {
  listLocationLevelHistory,
  LOCATION_OVERRIDE_RULE_BASE,
} from '../../../api/plan-location-override-rules-api'
import { useDeleteHistoryEntry } from '../../../hooks/useDeleteHistoryEntry'
import { useDeleteHistoryByFilter } from '../../../hooks/useDeleteHistoryByFilter'
import { LOCATION_OVERRIDE_RULE_PATTERN_FORMATTER } from './rule-templates'
import { US_STATE_OPTIONS } from '../../../constants'
import styles from '../../../components/PlanOverridesComponents.module.scss'

ModuleRegistry.registerModules([ServerSideRowModelModule])

const { RangePicker } = DatePicker

const SEARCH_DEBOUNCE_MS = 300
const DEFAULT_PAGE_SIZE = 20
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

const ACTION_TAG_PROPS: Partial<
  Record<PlanOverrideAction, { color: string; label: string }>
> = {
  [PlanOverrideAction.OVERRIDE_CREATED]: { color: 'green', label: 'Created' },
  [PlanOverrideAction.OVERRIDE_UPDATED]: { color: 'blue', label: 'Updated' },
  [PlanOverrideAction.OVERRIDE_STATUS_CHANGED]: {
    color: 'orange',
    label: 'Status changed',
  },
  [PlanOverrideAction.OVERRIDE_DELETED]: { color: 'red', label: 'Deleted' },
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function formatActorName(name: string | null): string {
  if (!name) return '—'
  if (UUID_RE.test(name)) return `${name.slice(0, 8)}…`
  return name
}

const ActionCellRenderer = ({
  data,
}: ICellRendererParams<LocationLevelHistoryEntryDto>) => {
  if (!data) return null
  const known = ACTION_TAG_PROPS[data.action as PlanOverrideAction]
  const isDocument = (data.action as string).includes('DOCUMENT')
  const tagColor = known?.color ?? (isDocument ? 'purple' : 'default')
  const tagLabel = known?.label ?? (isDocument ? 'Document' : 'Other')
  return <Tag color={tagColor}>{tagLabel}</Tag>
}

const ActorCellRenderer = ({
  data,
}: ICellRendererParams<LocationLevelHistoryEntryDto>) => {
  if (!data) return null
  return (
    <span className={styles.historyActorCell}>
      {formatActorName(data.actorName)}
    </span>
  )
}

const DEFAULT_COL_DEF: ColDef = {
  sortable: false,
  filter: false,
  resizable: true,
}

interface Props {
  onTotalChange?: (total: number) => void
  defaultPageSize?: number
  onPageSizeChange?: (size: number) => void
}

export default function LocationLevelHistoryContent({
  onTotalChange,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  onPageSizeChange,
}: Props) {
  const [stateFilter, setStateFilter] = useState<string | undefined>(undefined)
  const [searchInput, setSearchInput] = useState('')
  const [dateRange, setDateRange] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(null)

  const debouncedSearch = useDebounce(searchInput, SEARCH_DEBOUNCE_MS)
  const effectiveSearch =
    debouncedSearch.length >= 2 ? debouncedSearch : undefined

  const filters = useMemo<LocationLevelHistoryQueryDto>(
    () => ({
      state: stateFilter,
      search: effectiveSearch,
      occurredFrom: dateRange?.[0]?.toISOString() ?? undefined,
      occurredTo: dateRange?.[1]?.toISOString() ?? undefined,
    }),
    [stateFilter, effectiveSearch, dateRange]
  )

  const [total, setTotal] = useState(0)

  const onTotalChangeRef = useRef(onTotalChange)
  useEffect(() => {
    onTotalChangeRef.current = onTotalChange
  }, [onTotalChange])

  const filtersRef = useRef(filters)
  useEffect(() => {
    filtersRef.current = filters
  }, [filters])

  const [grid, setGrid] =
    useState<GridReadyEvent<LocationLevelHistoryEntryDto>>()
  const gridInitializedRef = useRef(false)

  const { mutateAsync: deleteEntry } = useDeleteHistoryEntry(
    LOCATION_OVERRIDE_RULE_BASE
  )
  const { mutateAsync: deleteByFilter } = useDeleteHistoryByFilter(
    LOCATION_OVERRIDE_RULE_BASE
  )

  const handleDeleteRow = useCallback(
    (logId: string) => {
      Modal.confirm({
        title: 'Delete history entry?',
        content: 'This action cannot be undone.',
        okText: 'Delete',
        okButtonProps: { danger: true },
        onOk: async () => {
          await deleteEntry(logId)
          grid?.api.refreshServerSide({ purge: true })
        },
      })
    },
    [deleteEntry, grid]
  )

  const handleDeleteByFilter = useCallback(() => {
    const hasFilters = Object.values(filtersRef.current).some(
      (v) => v !== undefined
    )
    Modal.confirm({
      title: 'Delete history entries?',
      content: hasFilters
        ? 'Delete all matching history entries? This action cannot be undone.'
        : 'Delete ALL history entries? This action cannot be undone.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        await deleteByFilter(filtersRef.current as Record<string, unknown>)
        grid?.api.refreshServerSide({ purge: true })
      },
    })
  }, [deleteByFilter, grid])

  const handleFirstDataRendered = useCallback(() => {
    gridInitializedRef.current = true
  }, [])

  const handlePaginationChanged = useCallback(
    (event: PaginationChangedEvent<LocationLevelHistoryEntryDto>) => {
      if (event.newPageSize && gridInitializedRef.current) {
        onPageSizeChange?.(event.api.paginationGetPageSize())
      }
    },
    [onPageSizeChange]
  )

  const datasource = useMemo(
    () => ({
      getRows: async (
        params: IServerSideGetRowsParams<LocationLevelHistoryEntryDto>
      ) => {
        try {
          const { startRow, endRow, sortModel } = params.request
          const pageSize = (endRow ?? DEFAULT_PAGE_SIZE) - (startRow ?? 0)
          const page = Math.floor((startRow ?? 0) / pageSize) + 1
          const sort = (sortModel as SortModelItem[])?.[0]
          const sortBy = sort?.colId as
            | LocationLevelHistorySortField
            | undefined
          const sortOrder = sort?.sort as 'asc' | 'desc' | undefined

          const result = await listLocationLevelHistory({
            ...filtersRef.current,
            page,
            limit: pageSize,
            ...(sortBy && sortOrder ? { sortBy, sortOrder } : {}),
          })

          onTotalChangeRef.current?.(result.meta.total)
          setTotal(result.meta.total)
          params.success({
            rowData: result.data,
            rowCount: result.meta.total,
          })
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to load location level history', error)
          params.fail()
        }
      },
    }),
    [filters]
  )

  useEffect(() => {
    if (!grid) return
    grid.api.setGridOption('serverSideDatasource', datasource)
    grid.api.refreshServerSide({ purge: true })
  }, [grid, datasource])

  const handleRangeChange = useCallback(
    (values: [Dayjs | null, Dayjs | null] | null) => {
      setDateRange(values)
    },
    []
  )

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams<LocationLevelHistoryEntryDto>) => [
      {
        name: 'Copy Cell',
        action: () => {
          const value = params.value
          if (value != null) void navigator.clipboard.writeText(String(value))
        },
      },
    ],
    []
  )

  const columnDefs = useMemo<ColDef<LocationLevelHistoryEntryDto>[]>(
    () => [
      {
        headerName: 'Action',
        field: 'action',
        cellRenderer: ActionCellRenderer,
        width: 140,
      },
      {
        headerName: 'State',
        field: 'matchState',
        cellRenderer: ({ value }: ICellRendererParams) =>
          value ? (
            <span>{value as string}</span>
          ) : (
            <span className={styles.historyEmptyCell}>—</span>
          ),
        width: 80,
      },
      {
        headerName: 'Zip Code',
        field: 'matchZipCode',
        cellRenderer: ({ value }: ICellRendererParams) =>
          value ? (
            <span>{value as string}</span>
          ) : (
            <span className={styles.historyEmptyCell}>—</span>
          ),
        width: 100,
      },
      {
        headerName: 'County FIPS',
        field: 'matchCountyFipsCode',
        cellRenderer: ({ value }: ICellRendererParams) =>
          value ? (
            <span>{value as string}</span>
          ) : (
            <span className={styles.historyEmptyCell}>—</span>
          ),
        width: 110,
      },
      {
        headerName: 'Change to',
        valueGetter: (p) => {
          if (!p.data) return ''
          try {
            const formatter =
              LOCATION_OVERRIDE_RULE_PATTERN_FORMATTER[
                p.data.ruleTemplate ?? 'UPSERT_RULE'
              ]
            if (!formatter) return '—'
            return formatter(p.data.params ?? {})
          } catch {
            return '—'
          }
        },
        cellRenderer: ({ value }: ICellRendererParams) => (
          <Tooltip title={value as string} placement="topLeft">
            <span className={styles.historyCellTruncated}>
              {value as string}
            </span>
          </Tooltip>
        ),
        flex: 2,
        minWidth: 200,
      },
      {
        headerName: 'Status',
        field: 'status',
        cellRenderer: ({
          data: row,
        }: ICellRendererParams<LocationLevelHistoryEntryDto>) => {
          if (!row) return null
          return (
            <Tag
              color={
                row.status === 'APPROVED'
                  ? 'green'
                  : row.status === 'REJECTED'
                    ? 'red'
                    : 'gold'
              }
            >
              {row.status === 'APPROVED'
                ? 'Approved'
                : row.status === 'REJECTED'
                  ? 'Rejected'
                  : 'Draft'}
            </Tag>
          )
        },
        width: 110,
      },
      {
        headerName: 'Occurred',
        field: 'occurredAt',
        valueFormatter: (p) =>
          p.value ? relativeTimeString(p.value as string, 30) : '—',
        sort: 'desc',
        sortable: true,
        width: 150,
      },
      {
        headerName: 'Actor',
        cellRenderer: ActorCellRenderer,
        flex: 1,
        minWidth: 180,
      },
      {
        headerName: '',
        colId: 'rowActions',
        width: 52,
        resizable: false,
        sortable: false,
        cellRenderer: ({
          data,
        }: ICellRendererParams<LocationLevelHistoryEntryDto>) => {
          if (!data) return null
          return (
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteRow(data.id)}
            />
          )
        },
      },
    ],
    [handleDeleteRow]
  )

  return (
    <>
      <div className={styles.historyFilterBar}>
        {total > 0 && (
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={handleDeleteByFilter}
            style={{ marginRight: 'auto' }}
          >
            Delete
          </Button>
        )}
        <Select
          placeholder="All states"
          allowClear
          showSearch
          optionFilterProp="label"
          options={US_STATE_OPTIONS}
          value={stateFilter}
          onChange={(v: string | undefined) => setStateFilter(v)}
          style={{ width: 120 }}
        />
        <Input
          placeholder="Search zip, county, FIPS…"
          value={searchInput}
          allowClear
          className={styles.historyFilterInput}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearchInput(e.target.value)
          }
        />
        <RangePicker
          onChange={handleRangeChange}
          placeholder={['From', 'To']}
          className={styles.historyFilterDatePicker}
        />
      </div>

      <div className={`ag-theme-alpine ${styles.historyGridAutoHeight}`}>
        <AgGridReact
          getRowId={({ data }) => data.id}
          rowModelType="serverSide"
          columnDefs={columnDefs}
          defaultColDef={DEFAULT_COL_DEF}
          enableCellTextSelection
          suppressRowClickSelection
          getContextMenuItems={getContextMenuItems}
          overlayLoadingTemplate="Loading..."
          overlayNoRowsTemplate="No history entries yet"
          domLayout="autoHeight"
          rowHeight={52}
          pagination
          paginationPageSize={defaultPageSize}
          paginationPageSizeSelector={PAGE_SIZE_OPTIONS}
          cacheBlockSize={defaultPageSize}
          onGridReady={setGrid}
          onFirstDataRendered={handleFirstDataRendered}
          onPaginationChanged={handlePaginationChanged}
        />
      </div>
    </>
  )
}
